import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
} from 'react';

/**
 * Registry that lets any page expose its data-fetch function so the Layout's
 * global "Atualizar" button can call it. Never touches auth state or reloads
 * the page — only retries the fetch the page registers.
 */
const RefreshContext = createContext(null);

export function RefreshProvider({ children }) {
  const refreshFnRef = useRef(null);
  const [refreshing, setRefreshing] = useState(false);

  const setRefreshFn = useCallback((fn) => {
    refreshFnRef.current = fn;
  }, []);

  const triggerRefresh = useCallback(async () => {
    const fn = refreshFnRef.current;
    if (!fn) return;
    setRefreshing(true);
    try {
      await fn();
    } finally {
      setRefreshing(false);
    }
  }, []);

  return (
    <RefreshContext.Provider value={{ setRefreshFn, triggerRefresh, refreshing }}>
      {children}
    </RefreshContext.Provider>
  );
}

/**
 * Call from any page to register its fetch function as the current refresh
 * target. Automatically unregisters on unmount.
 */
export function useRegisterRefresh(fn) {
  const ctx = useContext(RefreshContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setRefreshFn(fn);
    return () => ctx.setRefreshFn(null);
  }, [ctx, fn]);
}

export function useRefresh() {
  const ctx = useContext(RefreshContext);
  return {
    triggerRefresh: ctx?.triggerRefresh || (() => {}),
    refreshing: ctx?.refreshing || false,
  };
}
